import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import {
  SHARE_TARGETS,
  shareCopy,
  withoutUrl,
  type ShareContext,
  type ShareCopy,
} from '@/share/shareText'
import { siteSubject } from '@/share/embedSnippet'
import { track } from '@/share/ga'
import { PROVIDER_IDS } from '@/net/transport'
import { liveStore } from '@/store/useLive'
import { syncDialogOpen, trapTab } from './dialog'
import { EmbedBadge } from './EmbedBadge'

/**
 * The way out of the page and into a feed.
 *
 * This is the one component whose job is for the visitor to *leave*, and everything about it is
 * shaped by the five seconds it has to work in. Somebody watches a wall come down, feels the urge
 * to tell someone, and either the post is already written or the urge is gone. So:
 *
 *   - **The post is visible before it is sent.** The text sits in the sheet in the same words it
 *     will appear in on a timeline. Nobody sends a link they have not read, and a sheet that
 *     hides its own payload behind three network buttons is asking for trust it has not earned.
 *   - **`navigator.share` first, and synchronously.** On a phone the system sheet is faster than
 *     anything drawn here and it reaches the app the visitor actually posts from. It has to be
 *     called inside the click, not after an await, or every browser rejects it as untrusted —
 *     which is why `openShare` does that work itself rather than setting state and letting an
 *     effect do it.
 *   - **An abort is not an error.** Backing out of the system sheet means "not now", and popping
 *     our own dialog up in its place would be arguing with the visitor.
 *
 * Opened imperatively rather than by rendering a dialog per call site: there are six of them
 * (three squares, the header, the all-clear banner, the Codex sign) and one visitor can only be
 * looking at one sheet, so a singleton mounted once in `SiteChrome` is one dialog element instead
 * of six, and the focus and Escape handling has one owner.
 */

type Subscriber = () => void

const subscribers = new Set<Subscriber>()
let current: ShareContext | null = null

function publish(next: ShareContext | null): void {
  current = next
  for (const notify of [...subscribers]) notify()
}

function subscribe(notify: Subscriber): () => void {
  subscribers.add(notify)
  return () => {
    subscribers.delete(notify)
  }
}

function snapshot(): ShareContext | null {
  return current
}

/**
 * Share this. Call from a click handler — never from an effect or a timeout.
 *
 * `s=cp` for the native path: `docs/tasks/F20` defines exactly four sources and the system sheet
 * is indistinguishable from a copy by the time the link is pasted, so inventing a fifth letter
 * would put a value on public links that nothing else in the project knows how to read.
 */
export function openShare(context: ShareContext): void {
  const copy = shareCopy(context, 'cp')
  const nav = navigator as Navigator & {
    share?: (data: ShareData) => Promise<void>
    canShare?: (data: ShareData) => boolean
  }
  // The body without its trailing link, plus the link as its own field: that is the shape the Web
  // Share API is specified for, and passing the full post *and* the URL makes every target that
  // honours both show the link twice.
  const payload: ShareData = { title: copy.title, text: withoutUrl(copy), url: copy.url }

  if (typeof nav.share === 'function' && nav.canShare?.({ url: payload.url, text: payload.text })) {
    nav.share(payload).catch((error: unknown) => {
      // `AbortError` is the visitor closing the system sheet. Anything else — a target that
      // refused, a permissions policy in an iframe — leaves them with nothing, so fall back.
      if (error instanceof Error && error.name === 'AbortError') return
      publish(context)
    })
    return
  }
  publish(context)
}

export function closeShare(): void {
  publish(null)
}

/**
 * The trigger, wherever a state is worth boasting about.
 *
 * A component rather than six hand-rolled buttons so the label, the accessible name and the icon
 * are the same everywhere — a share affordance a visitor has to learn twice is one they use once.
 *
 * `context` may be a **function**, and on the two hottest surfaces it has to be. The header sits
 * on every page and the numbers it would boast about change twice a second; a button holding a
 * context *value* would force its parent to subscribe to the store and re-render the whole header
 * at 2 Hz to keep a string up to date that is only read when somebody clicks. Resolving at click
 * time costs nothing until the click and is also more correct — what gets posted is what was on
 * screen at the moment the button was pressed.
 */
export function ShareButton({
  context,
  label = 'Share',
  describe,
  className = 'mha-sharebtn',
}: {
  context: ShareContext | (() => ShareContext)
  label?: string
  /** What this particular button shares, for the accessible name. */
  describe: string
  className?: string
}) {
  return (
    <button
      type="button"
      className={className}
      onClick={() => openShare(typeof context === 'function' ? context() : context)}
    >
      <span aria-hidden="true" className="mha-sharebtn__icon">
        ↗
      </span>
      {/* The word, as its own element. The top bar hides it on a narrow phone, where the row is
          the wordmark plus these controls and there is not room for both; the sr-only clause
          below is what keeps the button's accessible name intact when it does. */}
      <span className="mha-sharebtn__label">{label}</span>
      <span className="sr-only"> — {describe}</span>
    </button>
  )
}

/**
 * What the whole site is doing right now, as something to post — read from the store rather than
 * subscribed to, so the header that renders this pays nothing per tick.
 *
 * A siege outranks the quiet card: with one provider down, "OpenAI is down and 4 812 of us are
 * yelling at a wall" is the post, and "all three are up, suspicious" is not.
 */
export function siteShareContext(): ShareContext {
  const state = liveStore.getState()
  if (state.helloReceived) {
    for (const id of PROVIDER_IDS) {
      const square = state.squares[id]
      if (square.phase !== 'siege') continue
      return {
        kind: 'siege',
        provider: id,
        clicks: state.counters.shown[id],
        zombies: square.zombies,
      }
    }
  }
  return { kind: 'peaceful', provider: 'all' }
}

/** Where a copy attempt got to. One string, rendered into a polite live region. */
type Status = { tone: 'ok' | 'warn'; message: string } | null

export function ShareSheet() {
  const context = useSyncExternalStore(subscribe, snapshot, snapshot)
  const ref = useRef<HTMLDialogElement>(null)
  const open = context !== null

  useEffect(() => {
    const dialog = ref.current
    if (dialog) syncDialogOpen(dialog, open)
  }, [open])

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDialogElement>) => trapTab(event), [])

  /** A click on the backdrop is reported against the dialog itself, so check the target. */
  const onClick = useCallback((event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) closeShare()
  }, [])

  return (
    <dialog
      ref={ref}
      className="mha-share"
      aria-labelledby="mha-share-title"
      onClose={closeShare}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      {/* Mounted by opening rather than hidden by closing: the panel reads the store once, and
          the number it puts in the post has to be the number that was on screen when the button
          was pressed, not when the page loaded. */}
      {context ? <Panel context={context} /> : null}
    </dialog>
  )
}

function Panel({ context }: { context: ShareContext }) {
  const [status, setStatus] = useState<Status>(null)
  const [embedOpen, setEmbedOpen] = useState(false)
  // Read once, at open. A counter that ticked while the sheet was up would rewrite the post the
  // visitor is in the middle of reading — and would not match the link they are about to send.
  const [ver] = useState(() => liveStore.getState().ver)
  const copy = shareCopy(context, 'cp')

  const reset = context.kind === 'codexReset' || context.kind === 'codexPage'
  const ogUrl = reset
    ? `/og.png?card=reset&v=${ver}&c=${context.clicks ?? 0}`
    : `/og.png?p=${context.provider}&v=${ver}`

  return (
    <div className="mha-share__panel" data-autofocus tabIndex={-1}>
      <div className="mha-share__head">
        <h2 id="mha-share-title" className="mha-share__title">
          Share this
        </h2>
        <button type="button" className="mha-share__close" onClick={closeShare}>
          <span aria-hidden="true">✕</span>
          <span className="sr-only">Close the share sheet</span>
        </button>
      </div>

      <OgPreview src={ogUrl} />

      {/*
        The post, readable and selectable. A `readOnly` textarea rather than a `<p>` so a visitor
        whose clipboard permission was denied — every locked-down corporate browser — can still
        select it by hand and get the same words out.
      */}
      <label className="mha-share__post-label" htmlFor="mha-share-post">
        The post
      </label>
      <textarea
        id="mha-share-post"
        className="mha-share__post"
        readOnly
        // Four, not three: on a 390 px phone the longest post wraps to four lines and the third
        // row clipped the URL mid-word — in the one box whose whole job is showing the visitor
        // exactly what they are about to send.
        rows={4}
        value={copy.text}
        onFocus={(event) => event.currentTarget.select()}
      />

      <ul className="mha-share__targets">
        {SHARE_TARGETS.map((target) => {
          const targeted = shareCopy(context, target.source)
          return (
            <li key={target.id}>
              <a
                className="mha-share__target"
                data-network={target.id}
                href={target.href(targeted)}
                target="_blank"
                rel="noopener noreferrer"
                // Two words per share: what it was about and where it went (`share/ga.ts`). Which
                // network actually moves traffic is the difference between guessing at the next
                // share text and knowing, and it is invisible in server logs — the click leaves for
                // x.com and comes back, days later, as a referrer we do not control.
                onClick={() => track('share', { context: context.kind, channel: target.id })}
              >
                {target.label}
                <span className="sr-only"> — open a post about this on {target.label}</span>
              </a>
            </li>
          )
        })}
      </ul>

      <div className="mha-share__actions">
        <CopyLink copy={copy} kind={context.kind} onStatus={setStatus} />
        <CopyImage src={ogUrl} onStatus={setStatus} />
        <button
          type="button"
          className="mha-share__action"
          aria-expanded={embedOpen}
          onClick={() => setEmbedOpen((was) => !was)}
        >
          {embedOpen ? 'Hide embed snippet' : 'Copy embed snippet'}
        </button>
      </div>

      {embedOpen ? <EmbedBadge subject={siteSubject(context.provider)} onStatus={setStatus} /> : null}

      {/* Polite, not assertive: a copy confirmation is a courtesy, and interrupting a screen
          reader mid-sentence to deliver one is not. */}
      <p className="mha-share__status" role="status" data-tone={status?.tone}>
        {status?.message ?? ''}
      </p>

      <p className="mha-share__disclaimer">
        Parody. Not affiliated with OpenAI, Anthropic or Google.
      </p>
    </div>
  )
}

/**
 * The link preview, shown rather than described.
 *
 * `/og.png` is B16 and does not exist yet, so today this always falls through to the static card
 * the frontend build ships (`docs/api.md` § 3). That is not a placeholder to be removed when B16
 * lands: the same fallback covers a renderer that is down, a pod without the fonts, and the CDN
 * miss that happens the first time a link is shared, and in every one of those cases a visitor
 * being shown a picture that is a little generic beats being shown a broken image icon in the
 * one dialog whose whole job is to look worth sending.
 */
function OgPreview({ src }: { src: string }) {
  const [current, setCurrent] = useState(src)
  const [gone, setGone] = useState(false)
  if (gone) return null
  return (
    <img
      className="mha-share__og"
      src={current}
      width={1200}
      height={630}
      alt="The link preview card for this page"
      onError={() => {
        if (current === FALLBACK_OG) setGone(true)
        else setCurrent(FALLBACK_OG)
      }}
    />
  )
}

const FALLBACK_OG = '/og/fallback.png'

function CopyLink({
  copy,
  kind,
  onStatus,
}: {
  copy: ShareCopy
  /** What was shared, so the counted event says more than "somebody copied something". */
  kind: ShareContext['kind']
  onStatus: (status: Status) => void
}) {
  return (
    <button
      type="button"
      className="mha-share__action"
      onClick={() => {
        track('share', { context: kind, channel: 'copy' })
        void writeText(copy.url).then((ok) =>
          onStatus(
            ok
              ? { tone: 'ok', message: 'Link copied.' }
              : { tone: 'warn', message: 'Could not reach the clipboard — select the post above.' },
          ),
        )
      }}
    >
      Copy link
    </button>
  )
}

/**
 * The image, on the clipboard, so it can be dropped straight into a post.
 *
 * Three things make this harder than it looks, and all three are handled below rather than
 * assumed away:
 *
 *   - **Safari's gesture budget.** By the time a `fetch` resolves, the click that authorised the
 *     clipboard write has expired. `ClipboardItem` accepts a *promise* of a blob for exactly this
 *     reason, so the item is constructed synchronously and the download resolves inside it.
 *   - **No `ClipboardItem` at all.** Some browsers still ship `navigator.clipboard` without it.
 *     There is no way to put an image on the clipboard there, so the honest fallback is to open
 *     the image, which leaves a right-click away from what was asked for.
 *   - **`/og.png` can 404.** It does today (B16). The static card is fetched instead, and if that
 *     is missing too the visitor is told rather than left with a button that did nothing.
 */
function CopyImage({ src, onStatus }: { src: string; onStatus: (status: Status) => void }) {
  return (
    <button
      type="button"
      className="mha-share__action"
      onClick={() => {
        const clipboard = navigator.clipboard as Clipboard | undefined
        const Item = (globalThis as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem
        if (!clipboard || typeof clipboard.write !== 'function' || Item === undefined) {
          openInTab(src)
          onStatus({ tone: 'warn', message: 'This browser cannot copy images — opened it instead.' })
          return
        }
        const blob = fetchCard(src)
        clipboard
          .write([new Item({ 'image/png': blob })])
          .then(() => onStatus({ tone: 'ok', message: 'Image copied.' }))
          .catch(() => {
            openInTab(src)
            onStatus({ tone: 'warn', message: 'Could not copy the image — opened it instead.' })
          })
      }}
    >
      Copy image
    </button>
  )
}

/** The live card, or the static one. Rejects only when neither exists. */
async function fetchCard(src: string): Promise<Blob> {
  const live = await fetch(src).catch(() => null)
  if (live?.ok) return live.blob()
  const fallback = await fetch(FALLBACK_OG)
  if (!fallback.ok) throw new Error(`no share image: ${src} and ${FALLBACK_OG} both failed`)
  return fallback.blob()
}

function openInTab(src: string): void {
  window.open(src, '_blank', 'noopener,noreferrer')
}

/** `false` rather than a throw: every caller's answer to a failed copy is the same message. */
export async function writeText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}
